
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload } from "lucide-react";

interface PrintableItem {
  id: string;
  category_id: string | null;
  title: string;
  description: string | null;
  zip_url: string;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
  is_free: boolean;
  created_at: string;
  updated_at: string;
}

interface PrintableCategory {
  id: string;
  name: string;
}

interface FormData {
  category_id: string;
  title: string;
  description: string;
  zip_url: string;
  image_url: string;
  display_order: number;
  is_active: boolean;
  is_free: boolean;
}

interface PrintableItemFormProps {
  dialogOpen: boolean;
  onDialogOpenChange: (open: boolean) => void;
  editingItem: PrintableItem | null;
  categories: PrintableCategory[];
  formData: FormData;
  setFormData: (data: FormData) => void;
  uploadingImage: boolean;
  onImageUpload: (file: File) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const PrintableItemForm = ({
  dialogOpen,
  onDialogOpenChange,
  editingItem,
  categories,
  formData,
  setFormData,
  uploadingImage,
  onImageUpload,
  onSubmit,
}: PrintableItemFormProps) => {
  return (
    <Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingItem ? 'Edit Item' : 'Add New Item'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Display Order</label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
          </div>

          {/* Image Upload Section */}
          <div className="space-y-3">
            <label className="block text-sm font-medium mb-2">Item Image</label>
            
            {/* Current Image Preview */}
            {formData.image_url && (
              <div className="border rounded-lg p-3 bg-gray-50">
                <img 
                  src={formData.image_url} 
                  alt="Preview" 
                  className="w-32 h-32 object-cover rounded-lg mb-2"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({...formData, image_url: ""})}
                >
                  Remove Image
                </Button>
              </div>
            )}

            {/* Upload Options */}
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onImageUpload(file);
                  }}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={uploadingImage}
                    asChild
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </span>
                  </Button>
                </label>
              </div>
              <div className="flex-1">
                <Input
                  placeholder="Or paste image URL"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">ZIP Download URL</label>
            <Input
              type="url"
              value={formData.zip_url}
              onChange={(e) => setFormData({...formData, zip_url: e.target.value})}
              placeholder="https://example.com/file.zip"
              required
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_free"
                checked={formData.is_free}
                onChange={(e) => setFormData({...formData, is_free: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="is_free" className="text-sm font-medium">Free Item</label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onDialogOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingItem ? 'Update' : 'Create'} Item
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PrintableItemForm;
